<template>
  <div data-testid="client-sso-tab" class="client-sso-tab">
    <div class="d-flex align-center mb-4">
      <div class="pl-2 title border-left-primary font-weight-thin">SSO</div>
      <v-spacer />
      <v-btn icon :loading="loading" :disabled="!clientId" data-testid="client-sso-tab__refresh" @click="refresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-alert
      v-if="error"
      type="error"
      text
      dense
      border="left"
      class="mb-4"
      dismissible
      data-testid="client-sso-tab__error"
      @input="clearError"
    >
      {{ error }}
    </v-alert>

    <v-skeleton-loader v-if="loading && !domain && !(domains || []).length" type="article" />

    <template v-else>
      <v-card flat outlined class="pa-4 mb-4">
        <HostingModePanel
          :current-mode="hostingMode"
          :is-app-host-sso-admin-client="isAppHostSsoAdminClient"
          :has-dedicated-domain="isDedicated"
          :existing-hostname="domainHostname"
          :existing-slug="domainSlug"
          :saving="saving"
          @save="onHostingModeSave"
        />
      </v-card>

      <v-card flat outlined class="pa-4 mb-4">
        <DomainAuthPanel
          :editable="canEditAuthPolicy"
          :read-only-shared="isShared && !isAppHostSsoAdminClient"
          :auth-policy="domainAuthPolicy"
          :break-glass-user-ids="domainBreakGlass"
          :hostname="authHostname"
          :cognito-app-client-id="cognitoAppClientId"
          :client-secret-arn="clientSecretArn"
          :region="domainRegion"
          :callback-urls="domainCallbackUrls"
          :logout-urls="domainLogoutUrls"
          :provisioning-version="provisioningVersion"
          :enable-sso-jit="domainEnableSsoJit"
          :saving="saving"
          @save="onAuthPolicySave"
        />
      </v-card>

      <v-card flat outlined class="pa-4 mb-4">
        <DomainsPanel
          :domains="domains"
          :client-ids="domainClientIds"
          :app-host-domain-id="appHostDomainId"
          :is-shared="isShared"
          :is-app-host-sso-admin-client="isAppHostSsoAdminClient"
          :can-manage-dedicated-domains="isDedicated"
          :saving="saving"
          @add-client-id="onAddClientId"
          @remove-client-id="onRemoveClientId"
          @add-domain="onAddDomain"
          @retire-domain="onRetireDomain"
        />
      </v-card>

      <v-card flat outlined class="pa-4 mb-4">
        <ConnectionsPanel
          :connections="connections"
          :hostname="connectionsHostname"
          :editable="canEditConnections"
          :saving="saving"
          @create="onCreateConnection"
          @update="onUpdateConnection"
          @test="onTestConnection"
          @activate="onActivateConnection"
          @disable="onDisableConnection"
          @retire="onRetireConnection"
        />
      </v-card>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import constants from '@/plugins/constants';
import { getAppHost } from '@/plugins/hostMode';
import HostingModePanel from './sso/HostingModePanel.vue';
import DomainAuthPanel from './sso/DomainAuthPanel.vue';
import DomainsPanel from './sso/DomainsPanel.vue';
import ConnectionsPanel from './sso/ConnectionsPanel.vue';

const { SSO } = constants;
const HOSTING_MODES = SSO.HOSTING_MODES;

export default {
  name: 'ClientSsoTab',
  components: {
    HostingModePanel,
    DomainAuthPanel,
    DomainsPanel,
    ConnectionsPanel,
  },
  props: {
    clientId: { type: String, default: '' },
    clientName: { type: String, default: '' },
    isAppHostSsoAdminClient: { type: Boolean, default: false },
  },
  computed: {
    ...mapState('admin/sso', [
      'domain',
      'domains',
      'connections',
      'loading',
      'saving',
      'error',
    ]),
    ...mapGetters('admin/sso', ['primaryDomain', 'activeHostname']),
    hostingMode() {
      return (
        this.domain?.hostingMode ||
        this.primaryDomain?.hostingMode ||
        HOSTING_MODES.SHARED_APP_HOST
      );
    },
    isDedicated() {
      return this.hostingMode === HOSTING_MODES.DEDICATED_SUBDOMAIN;
    },
    isShared() {
      return !this.isDedicated;
    },
    canEditAuthPolicy() {
      return this.isDedicated || this.isAppHostSsoAdminClient;
    },
    canEditConnections() {
      return this.isDedicated || this.isAppHostSsoAdminClient;
    },
    domainHostname() {
      return this.domain?.hostname || this.primaryDomain?.hostname || '';
    },
    domainSlug() {
      return this.domain?.slug || this.primaryDomain?.slug || '';
    },
    domainAuthPolicy() {
      return this.domain?.authPolicy || this.primaryDomain?.authPolicy || 'HYBRID';
    },
    domainBreakGlass() {
      return (
        this.domain?.breakGlassUserIds ||
        this.primaryDomain?.breakGlassUserIds ||
        []
      );
    },
    domainEnableSsoJit() {
      const d = this.domain || this.primaryDomain;
      return d?.enableSsoJit === true;
    },
    cognitoAppClientId() {
      return (
        this.domain?.cognitoAppClientId ||
        this.primaryDomain?.cognitoAppClientId ||
        ''
      );
    },
    clientSecretArn() {
      return (
        this.domain?.clientSecretArn ||
        this.primaryDomain?.clientSecretArn ||
        ''
      );
    },
    domainRegion() {
      return this.domain?.region || this.primaryDomain?.region || '';
    },
    domainCallbackUrls() {
      return (
        this.domain?.callbackUrls ||
        this.primaryDomain?.callbackUrls ||
        []
      );
    },
    domainLogoutUrls() {
      return this.domain?.logoutUrls || this.primaryDomain?.logoutUrls || [];
    },
    provisioningVersion() {
      return (
        this.domain?.provisioningVersion ||
        this.primaryDomain?.provisioningVersion ||
        ''
      );
    },
    domainClientIds() {
      return (
        this.domain?.clientIds ||
        this.primaryDomain?.clientIds ||
        []
      );
    },
    appHostDomainId() {
      if (!this.isAppHostSsoAdminClient) return '';
      const appHost = getAppHost();
      const match = (this.domains || []).find(
        (d) =>
          d?.hostingMode === HOSTING_MODES.SHARED_APP_HOST ||
          (appHost && String(d?.hostname || '').toLowerCase() === appHost),
      );
      return (
        match?._id ||
        match?.id ||
        this.domain?._id ||
        this.domain?.id ||
        this.primaryDomain?._id ||
        this.primaryDomain?.id ||
        ''
      );
    },
    authHostname() {
      if (this.isDedicated) return this.domainHostname;
      if (this.isAppHostSsoAdminClient) {
        return this.domainHostname || getAppHost();
      }
      return getAppHost();
    },
    connectionsHostname() {
      if (!this.canEditConnections) return '';
      return this.authHostname || '';
    },
    activeDomainId() {
      return (
        this.domain?._id ||
        this.domain?.id ||
        this.primaryDomain?._id ||
        this.primaryDomain?.id ||
        ''
      );
    },
  },
  watch: {
    clientId: {
      immediate: true,
      handler(id) {
        if (id) this.refresh();
        else this.$store.commit('admin/sso/reset');
      },
    },
  },
  beforeDestroy() {
    this.$store.commit('admin/sso/reset');
  },
  methods: {
    clearError() {
      this.$store.commit('admin/sso/setError', null);
    },
    async refresh() {
      if (!this.clientId) return;
      try {
        await this.$store.dispatch('admin/sso/fetchDomains', {
          axiosInstance: this.axios,
          clientId: this.clientId,
        });
        if (this.canEditConnections && this.connectionsHostname) {
          await this.$store.dispatch('admin/sso/fetchConnections', {
            axiosInstance: this.axios,
            hostname: this.connectionsHostname,
            clientId: this.clientId,
          });
        } else {
          this.$store.commit('admin/sso/setConnections', []);
        }
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to load SSO settings');
      }
    },
    async onHostingModeSave({ hostingMode, hostname, slug }) {
      try {
        if (hostingMode === HOSTING_MODES.DEDICATED_SUBDOMAIN && !this.isDedicated) {
          await this.$store.dispatch('admin/sso/bootstrapDomain', {
            axiosInstance: this.axios,
            clientId: this.clientId,
            payload: {
              hostname,
              slug,
              hostingMode,
              authPolicy: 'HYBRID',
              callbackUrls: [`https://${hostname}${SSO.CALLBACK_PATH}`],
              logoutUrls: [`https://${hostname}${SSO.LOGOUT_COMPLETE_PATH}`],
              idempotencyKey: `boot-${slug || hostname}-${Date.now()}`,
            },
          });
          this.$store.commit('sbSuccess', 'Dedicated domain bootstrapped');
        } else if (this.activeDomainId) {
          await this.$store.dispatch('admin/sso/updateDomain', {
            axiosInstance: this.axios,
            domainId: this.activeDomainId,
            clientId: this.clientId,
            payload: { hostingMode },
          });
          this.$store.commit('sbSuccess', 'Hosting mode updated');
        } else if (hostingMode === HOSTING_MODES.SHARED_APP_HOST) {
          this.$store.commit(
            'sbInfo',
            'Shared app host is the default. Add this client to app-host clientIds from Consark Development.',
          );
        }
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to save hosting mode');
      }
    },
    async onAuthPolicySave(payload) {
      if (!this.activeDomainId) {
        this.$store.commit('sbError', 'No SSO domain to update');
        return;
      }
      try {
        await this.$store.dispatch('admin/sso/updateDomain', {
          axiosInstance: this.axios,
          domainId: this.activeDomainId,
          clientId: this.clientId,
          payload,
        });
        this.$store.commit('sbSuccess', 'Auth policy updated');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to update auth policy');
      }
    },
    async onAddClientId({ domainId, targetClientId }) {
      try {
        await this.$store.dispatch('admin/sso/addClientId', {
          axiosInstance: this.axios,
          domainId,
          clientId: this.clientId,
          targetClientId,
        });
        this.$store.commit('sbSuccess', 'Client added to app-host domain');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to add clientId');
      }
    },
    async onRemoveClientId({ domainId, targetClientId }) {
      try {
        await this.$store.dispatch('admin/sso/removeClientId', {
          axiosInstance: this.axios,
          domainId,
          clientId: this.clientId,
          targetClientId,
        });
        this.$store.commit('sbSuccess', 'Client removed from app-host domain');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to remove clientId');
      }
    },
    async onAddDomain(payload) {
      try {
        await this.$store.dispatch('admin/sso/addDomain', {
          axiosInstance: this.axios,
          clientId: this.clientId,
          payload,
        });
        this.$store.commit('sbSuccess', 'Domain created');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to create domain');
      }
    },
    async onRetireDomain({ domainId, reason, reserveDays }) {
      try {
        await this.$store.dispatch('admin/sso/retireDomain', {
          axiosInstance: this.axios,
          domainId,
          clientId: this.clientId,
          reason,
          reserveDays,
        });
        this.$store.commit('sbSuccess', 'Domain retired');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to retire domain');
      }
    },
    async onCreateConnection(payload) {
      try {
        await this.$store.dispatch('admin/sso/createConnection', {
          axiosInstance: this.axios,
          clientId: this.clientId,
          payload,
        });
        this.$store.commit('sbSuccess', 'Connection created');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to create connection');
      }
    },
    async onUpdateConnection({ connectionId, payload }) {
      try {
        await this.$store.dispatch('admin/sso/updateConnection', {
          axiosInstance: this.axios,
          connectionId,
          clientId: this.clientId,
          payload,
        });
        this.$store.commit('sbSuccess', 'Connection updated');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to update connection');
      }
    },
    async onTestConnection({ connectionId }) {
      try {
        await this.$store.dispatch('admin/sso/testConnection', {
          axiosInstance: this.axios,
          connectionId,
          clientId: this.clientId,
        });
        this.$store.commit('sbSuccess', 'Connection test started');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to test connection');
      }
    },
    async onActivateConnection({ connectionId }) {
      try {
        await this.$store.dispatch('admin/sso/activateConnection', {
          axiosInstance: this.axios,
          connectionId,
          clientId: this.clientId,
        });
        this.$store.commit('sbSuccess', 'Connection activated');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to activate connection');
      }
    },
    async onDisableConnection({ connectionId }) {
      try {
        await this.$store.dispatch('admin/sso/disableConnection', {
          axiosInstance: this.axios,
          connectionId,
          clientId: this.clientId,
        });
        this.$store.commit('sbSuccess', 'Connection disabled');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to disable connection');
      }
    },
    async onRetireConnection({ connectionId, reason, confirm }) {
      try {
        await this.$store.dispatch('admin/sso/retireConnection', {
          axiosInstance: this.axios,
          connectionId,
          clientId: this.clientId,
          reason,
          confirm,
        });
        this.$store.commit('sbSuccess', 'Connection retired');
        await this.refresh();
      } catch (err) {
        this.$store.commit('sbError', err.message || 'Failed to retire connection');
      }
    },
  },
};
</script>
